import { FC, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ConfirmDelete from "../../../../../components/common/ConfirmDelete";
import FormDateField from "../../../../../components/forms/FormDateField";
import FormPasswordField from "../../../../../components/forms/FormPasswordField";
import FormSectionTitle from "../../../../../components/forms/FormSectionTitle";
import FormSelect from "../../../../../components/forms/FormSelect";
import FormTextField from "../../../../../components/forms/FormTextField";
import {
	emptyUpsertUserPayload,
	UpsertUserPayload,
	useUserValidationSchemas,
} from "../../../../../datasources/requests/adminUsers";
import { GetOptionsTopic } from "../../../../../datasources/requests/common";
import { UserRegistry } from "../../../../../datasources/responses/adminUsers";
import { useServerOptions, useStaticOptions } from "../../../../../hooks/useOptions";

interface Props {
	id?: string;
	isOpen: boolean;
	onCancel: () => void;
	onSuccess: () => void;
	user: UserRegistry;
	userIsLoading: boolean;
	handleClickSend: (payload: UpsertUserPayload) => void;
	askConfirmDelete: boolean;
	setAskConfirmDelete: (value: boolean) => void;
	confirmDelete: () => void;
	closeConfirmDelete: () => void;
}

/**
 * UserDetails render the UI component that allows users to create, update or delete other users.
 * @param id
 * @param isOpen
 * @param onCancel
 * @param onSuccess
 * @constructor
 */
const UserDetails: FC<Props> = ({
	id,
	isOpen,
	onCancel,
	onSuccess,
	user,
	userIsLoading,
	handleClickSend,
	askConfirmDelete,
	setAskConfirmDelete,
	confirmDelete,
	closeConfirmDelete,
}) => {
	const { t } = useTranslation("feature.users");
	const { languages, userStatuses } = useStaticOptions();
	const { UpsertUserSchema } = useUserValidationSchemas();
	const [showPassword, setShowPassword] = useState(false);

	const { data: roles, isLoading: rolesAreLoading } = useServerOptions(GetOptionsTopic.Roles);
	const form = useForm<UpsertUserPayload>({
		defaultValues: { ...emptyUpsertUserPayload },
		resolver: zodResolver(UpsertUserSchema),
	});
	const toggleShowPassword = () => setShowPassword(!showPassword);
	const email = form.watch("email");
	const password = form.watch("password");

	useEffect(() => {
		form.reset(
			{
				email: user?.email,
				roleId: user?.roleId,
				status: user?.status,
				blocked: user?.blocked,
				username: user?.username,
				lastName: user?.lastName,
				firstName: user?.firstName,
				telephone: user?.telephone,
				password: "",
				passwordConfirm: "",
				preferredLanguage: user?.preferredLanguage,
				expiredAt: user?.expiredAt ? dayjs(user.expiredAt) : undefined,
				multiFactorAuthenticationEnabled: user?.multiFactorAuthenticationEnabled,
			},
			{ keepErrors: true }
		);
	}, [user, form]);

	useEffect(() => {
		form.trigger("passwordConfirm");
	}, [form, password]);

	// const isLoading = rolesAreLoading || userIsLoading;
	const isLoading = true;

	return (
		<>
			<Grid container spacing={2}>
				<FormSectionTitle title={t("registry")} noPaddingTop />
				<Grid size={4}>
					<FormTextField
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						readOnly={user?.isProtected}
						rules={{ required: true }}
						form={form}
						name="firstName"
						label={t("first name")}
					/>
				</Grid>
				<Grid size={4}>
					<FormTextField
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						readOnly={user?.isProtected}
						rules={{ required: true }}
						form={form}
						name="lastName"
						label={t("last name")}
					/>
				</Grid>
				<Grid size={4}>
					<FormTextField
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						readOnly={user?.isProtected}
						rules={{ required: true }}
						form={form}
						name="telephone"
						label={t("telephone")}
					/>
				</Grid>

				<FormSectionTitle title={t("account")} />
				<Grid size={user?.isProtected ? 6 : 4}>
					<FormTextField
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						readOnly={user?.isProtected}
						form={form}
						name="username"
						label={t("username")}
					/>
				</Grid>
				<Grid size={user?.isProtected ? 6 : 4}>
					<FormTextField
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						readOnly={user?.isProtected}
						autoComplete="off"
						form={form}
						name="email"
						label={t("email")}
					/>
				</Grid>

				{!user?.isProtected && (
					<Grid size={4}>
						<FormPasswordField
							autoComplete="off"
							onToggleShowPassword={toggleShowPassword}
							form={form}
							name="password"
							label={t("password")}
							fullWidth
							isLoading={isLoading}
							showPassword={showPassword}
						/>
					</Grid>
				)}
				<Grid size={user?.isProtected ? 6 : 4}>
					<FormSelect
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						readOnly={user?.isProtected}
						name="roleId"
						form={form}
						label={t("role")}
						options={roles?.options ?? []}
					/>
				</Grid>
				<Grid size={user?.isProtected ? 6 : 4}>
					<FormSelect
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						name="status"
						form={form}
						label={t("status")}
						options={userStatuses}
						readOnly={user?.isProtected}
					/>
				</Grid>

				{!user?.isProtected && (
					<Grid size={4}>
						<FormPasswordField
							autoComplete="off"
							fullWidth
							isLoading={isLoading}
							showPassword={showPassword}
							form={form}
							name="passwordConfirm"
							label={t("confirm password")}
						/>
					</Grid>
				)}

				<FormSectionTitle title={t("preferences")} />
				<Grid size={6}>
					<FormSelect
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						name="preferredLanguage"
						form={form}
						label={t("preferred language")}
						readOnly={user?.isProtected}
						options={languages}
					/>
				</Grid>
				<Grid size={6}>
					<FormDateField
						fullWidth
						disabled={isLoading}
						isLoading={isLoading}
						readOnly={user?.isProtected}
						form={form}
						name="expiredAt"
						label={t("expires at")}
					/>
				</Grid>
				{!user?.isProtected && (
					<Grid size={12} textAlign="end">
						<Stack direction="row-reverse" spacing={2}>
							{id && (
								<Button
									className="cancel"
									loading={isLoading}
									loadingPosition="start"
									onClick={() => setAskConfirmDelete(true)}
								>
									{t("delete", { ns: "common" })}
								</Button>
							)}
							<Button
								className="confirm"
								loading={isLoading}
								loadingPosition="start"
								onClick={() => handleClickSend(form.getValues())}
								disabled={!form.formState.isValid}
							>
								{t("apply", { ns: "common" })}
							</Button>
						</Stack>
					</Grid>
				)}
			</Grid>
			{id && (
				<ConfirmDelete
					confirmCode={email}
					isOpen={askConfirmDelete}
					onConfirm={confirmDelete}
					confirmLabel={t("email")}
					onCancel={closeConfirmDelete}
					title={t("do you really want to delete this user?")}
					confirmHint={t("type email {email}, then confirm to delete user", { email })}
				/>
			)}
		</>
	);
};

export default UserDetails;
